import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';

// Import tab components
import { DefaultTab } from '../components/dashboard/DefaultTab';
import { ScheduledTab } from '../components/dashboard/ScheduledTab';
import { SpotOrdersTab } from '../components/dashboard/SpotOrdersTab';
import { POLineDashboardTab } from '../components/dashboard/POLineDashboardTab';
import { CachedPerformanceTab } from '../components/dashboard/CachedPerformanceTab';
import { GroupKPITab } from '../components/dashboard/GroupKPITab';
import { GroupDetailKPITab } from '../components/dashboard/GroupDetailKPITab';
import { SiteKPITab } from '../components/dashboard/SiteKPITab';
import { CostTab } from '../components/dashboard/CostTab';
import { EmissionsTab } from '../components/dashboard/EmissionsTab';
import { ComplianceTab } from '../components/dashboard/ComplianceTab';
import { FutureForecastTab } from '../components/dashboard/FutureForecastTab';
import { ExpeditingTab } from '../components/dashboard/ExpeditingTab';
import { PerformanceRatingsTab } from '../components/dashboard/PerformanceRatingsTab';
import { DateAdherenceTab } from '../components/dashboard/DateAdherenceTab';
import { PerformanceTab } from '../components/dashboard/PerformanceTab';

// Import dashboard UI components
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardFilterBar } from '../components/dashboard/DashboardFilterBar';
import { countActiveFilters } from '../components/dashboard/dashboardFilterUtils';
import { leaveCostTab } from '../components/dashboard/costTabFilters';
import { costViewFilters } from '../components/dashboard/costViewRanges';
import { useCostView } from '../components/dashboard/useCostView';
import { readSessionTab, writeSessionTab } from '../components/dashboard/dashboardSession';
import { DashboardFilters } from '../components/dashboard/DashboardFilters';
import { SaveFilterDialog } from '../components/dashboard/SaveFilterDialog';
import { Toast } from '../components/dashboard/Toast';

// Import types
import { DASHBOARD_TAB_REGISTRY } from '../components/dashboard/types';

// Import hooks and contexts
import { useAuth } from '../contexts/AuthContext';
import { useView } from '../contexts/ViewContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { usePortalMode } from '../hooks/usePortalMode';
import { useDashboardData } from '../hooks/useDashboardData';

// Dashboard tabs — driven from shared registry
const tabs = DASHBOARD_TAB_REGISTRY;

// Tabs that fetch their own payload and take no `dashboard.data` prop. They read
// none of `orders/dashboard_stats/` — the heaviest call on the dashboard, ~6.4s
// cold on a schedule-line tenant's dyno — so it is neither awaited nor issued
// while one of them is showing. Two separate wins from the same list:
//   * the page is not blanked by the shared load, so the tab mounts and can
//     start its own request immediately rather than queueing behind it;
//   * the call is skipped outright, so the dyno does not do the work at all.
// Data-dependent tabs still wait for it and still trigger it on switch.
const SELF_FETCHING_TABS = ['scheduled', 'spot-orders', 'po-lines'];

export default function Dashboard() {
  const { user } = useAuth();
  const { mainMenuType, isComplianceMode, isDashboardTabEnabled, loadSettingsFromBackend, usesScheduleLines, deliveryGrainCustomerId, isFeatureEnabled, isLoading: menuLoading } = useMenuSettings();
  // CR-8241: Future Forecast reads PO schedules, which the API refuses (403) unless the
  // company has schedule collaboration on. Hidden once settings say it is off; while
  // they load it keeps its place (a remembered tab is not dropped) but does not mount.
  const forecastEnabled = isFeatureEnabled('schedule_collaboration');
  const { viewLevel, selectedCompanyName, selectedSiteName, selectedGroupName, selectedGroupId, accessibleCompanies, companyGroup } = useView();
  const { isSupplier: portalIsSupplier, supportsDualMode: portalSupportsDualMode } = usePortalMode();

  // CR-5454: a refresh reopens the tab the user was on (filters: useDashboardData).
  const [activeTab, setActiveTab] = useState(() => (
    isComplianceMode ? 'compliance' : (readSessionTab(user?.id) ?? 'default')
  ));
  useEffect(() => {
    if (!isComplianceMode) writeSessionTab(user?.id, activeTab);
  }, [activeTab, user?.id, isComplianceMode]);
  const [showFilters, setShowFilters] = useState(false);

  // CR-4441: Refresh settings on mount to pick up any admin changes
  useEffect(() => {
    loadSettingsFromBackend();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dashboard = useDashboardData(isComplianceMode, !SELF_FETCHING_TABS.includes(activeTab), activeTab === 'cost');
  // CR-8611: Cost's Future / Historical sub-tab — decides which date ranges fit.
  const { costView, setCostView } = useCostView(user?.id, activeTab, dashboard.filters, dashboard.setFilters);

  // CR-4349: Derive unique divisions from accessible companies for the filter dropdown.
  // CR-4887: ViewContext.accessibleCompanies stays as the SUPPLIER hierarchy
  // (the user-menu site switcher reads it). Dashboard divisions need the
  // CUSTOMER hierarchy when the user is on the supplier portal — use the
  // dashboard hook's customers list, which is fetched with
  // ?context=customer_filter and contains the customer companies served.
  const divisionSource = portalIsSupplier ? dashboard.customers : accessibleCompanies;
  const divisions = useMemo(() => {
    const divMap = new Map<number, string>();
    for (const company of divisionSource) {
      const rec = company as unknown as Record<string, unknown>;
      const groupId = rec['group'] as number | undefined;
      const groupName = rec['group_name'] as string | undefined;
      if (groupId && groupName) {
        divMap.set(groupId, groupName);
      }
    }
    return Array.from(divMap.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [divisionSource]);

  const isAdminUser = user?.is_superuser || user?.is_staff;
  const effectiveIsSupplier = (isAdminUser && !portalSupportsDualMode)
    ? mainMenuType === '2'
    : portalIsSupplier;

  const COMPLIANCE_HIDDEN_TABS = ['default', 'po-lines', 'group-kpi', 'group-detail-kpi', 'site-kpi', 'cost', 'emissions', 'future-forecast', 'date-adherence', 'expediting', 'performance-ratings', 'performance'];

  const deliveryMode = dashboard.data?.delivery_metric_mode ?? 'both'
  const showOTD = deliveryMode === 'both' || deliveryMode === 'otd_only'

  const visibleTabs = tabs.filter(tab => {
    // CR-4441: Admin setting controls which tabs are enabled (split by role)
    if (!isDashboardTabEnabled(tab.id, effectiveIsSupplier)) return false;
    if (isComplianceMode && COMPLIANCE_HIDDEN_TABS.includes(tab.id)) return false;
    // CR-6381: Performance Ratings tab now visible for both suppliers AND customers
    // Suppliers see their own ratings; customers see rating distribution charts
    if ((tab.id === 'future-forecast' || tab.id === 'expediting') && effectiveIsSupplier) return false;
    if (tab.id === 'future-forecast' && !menuLoading && !forecastEnabled) return false;
    // CR-4751: Cached Performance data is captured customer-side only
    if (tab.id === 'cached-performance' && effectiveIsSupplier) return false;
    // Group tabs visible when user's company belongs to a group (any view level)
    // Only hide if no group context exists at all
    if (tab.id === 'group-kpi' && !selectedGroupId && viewLevel === 'SITE') return false;
    if (tab.id === 'group-detail-kpi' && !selectedGroupId && viewLevel === 'SITE') return false;
    if (tab.id === 'date-adherence' && !showOTD) return false;
    // PO Line Dashboard is the PO-line-grain sibling of the Scheduled Dashboard.
    // A single-customer scope shows only the tabs of that customer's effective
    // delivery grain; a mixed scope (group / helix roll-up, no resolved customer)
    // keeps both so each grain's numbers stay reachable.
    const singleCustomerScope = deliveryGrainCustomerId !== null;
    if (tab.id === 'po-lines' && usesScheduleLines && singleCustomerScope) return false;
    if ((tab.id === 'scheduled' || tab.id === 'spot-orders') && !usesScheduleLines && singleCustomerScope) return false;
    return true;
  });

  // CR-4166 & CR-4441: If active tab is no longer visible, fallback.
  // In compliance mode, always prefer compliance tab as default.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(t => t.id === activeTab)) {
      const complianceTab = isComplianceMode
        ? visibleTabs.find(t => t.id === 'compliance')
        : undefined;
      setActiveTab(complianceTab ? complianceTab.id : visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab, isComplianceMode]);

  // The filter panel is opened by the user (Show filters) on every tab,
  // including Site KPI — it no longer pops open on its own when the tab is
  // selected (the auto-open from CR-5912 got in the way of the KPI view).

  // Lazy-load per-tab data slices on demand — keeps the default dashboard
  // payload small. Each loader is sticky: once fetched, subsequent refreshes
  // keep the section hydrated.
  // CR-5454: the Cost tab opens on Next 20 weeks (when the range is still the
  // page default) and its Contract filters exist on that tab only — both are
  // undone on the way out, since no other tab offers them.
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    const leftCost = prevTabRef.current === 'cost' && activeTab !== 'cost';
    prevTabRef.current = activeTab;
    if (!leftCost) return;
    const next = leaveCostTab(dashboard.filters);
    if (next) dashboard.setFilters(next);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'date-adherence') {
      dashboard.loadDateAdherence();
    } else if (activeTab === 'site-kpi') {
      dashboard.loadCompanyKpi();
    } else if (activeTab === 'group-kpi') {
      dashboard.loadGroupKpi();
    } else if (activeTab === 'group-detail-kpi') {
      // Group Detail KPI charts read the per-site breakdown (siteKPI) as
      // well as the group roll-up — without the company-KPI slice the tab
      // rendered "No data for the selected period" until Site KPI was opened.
      dashboard.loadGroupKpi();
      dashboard.loadCompanyKpi();
    } else if (activeTab === 'cost') {
      // CR-5454: always refetch on entry — the cached cost slice may have been
      // built on another tab, without the Cost filters. CR-8611: with a range
      // that suits the sub-tab (Future: Next 20 weeks by default, Historical:
      // Last 12 months) — replaces the old "page default becomes Next 20 weeks".
      const next = costViewFilters(costView, dashboard.filters);
      if (next) dashboard.setFilters(next);
      dashboard.loadCostData(next ?? dashboard.filters);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable identities. Both are passed to every tab component; rebuilding them
  // on each render made a new prop object per keystroke, so React.memo on a tab
  // could never hold. Cheap on their own, and a precondition for memoising the
  // heavy chart tabs.
  const currency = useMemo(
    () => ({ currentCurrency: dashboard.filters.currency }),
    [dashboard.filters.currency],
  );

  const handleDrillDown = useCallback((_type: string, _data?: Record<string, unknown>) => {
    // Drill-down navigation placeholder
  }, []);

  // A refetch with data already on screen: keep the dashboard rendered and say
  // so, rather than replacing it with the full-screen spinner.
  const isRevalidating = dashboard.loading && !!dashboard.data;

  const renderTabContent = () => {
    // Self-fetching tabs are resolved FIRST, before the shared-payload guard.
    // They own their data and never read `dashboard.data`, so its absence says
    // nothing about them — and they now render before the shared load finishes
    // (and, for the heaviest call, instead of it). That is precisely the state
    // the guard below assumed impossible, which stranded the landing view on
    // "No dashboard data available" behind a Reload button that fixed nothing.
    // Returning here also keeps the null-narrowing the switch below relies on.
    switch (activeTab) {
      case 'scheduled':
        return <ScheduledTab />;
      case 'spot-orders':
        return <SpotOrdersTab />;
      case 'po-lines':
        return <POLineDashboardTab />;
      default:
        break;
    }

    if (!dashboard.data) {
      // Reachable only once `dashboard.loading` is false for a tab that does
      // consume the payload — the loading spinner returns earlier for those. So
      // no fetch is in flight here and nothing will resolve on its own: never
      // render a spinner in this state.
      if (dashboard.error) {
        return (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Failed to load dashboard data</p>
              <p className="text-red-600 text-sm mt-1">{dashboard.error}</p>
              <button onClick={() => dashboard.fetchDashboardData(true)} className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Retry</button>
            </div>
          </div>
        );
      }
      // No data and no error: the fetch was cancelled or superseded before any
      // data landed. Offer a way out rather than stranding the user.
      return (
        <div className="p-6 flex items-center justify-center min-h-[300px]">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">No dashboard data available.</p>
            <button onClick={() => dashboard.fetchDashboardData(true)} className="mt-3 px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90">Reload</button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'default':
        return <DefaultTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} dataPeriod={dashboard.dataPeriod} orderType={dashboard.orderType} />;
      case 'cached-performance':
        return <CachedPerformanceTab />;
      case 'group-kpi':
        return <GroupKPITab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      case 'group-detail-kpi':
        return <GroupDetailKPITab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} orderType={dashboard.orderType} />;
      case 'site-kpi':
        // CR-5912: Site KPI now uses the page-level DashboardFilters (force-opened
        // by the effect below). The previously-embedded supplier-type dropdown and
        // its onSupplierTypeChange callback were removed.
        // CR-5868: suppliers see the tab too; isSupplier relabels the by-site
        // charts (the axis is the customer site on the supplier portal).
        return <SiteKPITab
          data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown}
          isSupplier={effectiveIsSupplier} orderType={dashboard.orderType}
        />;
      case 'cost':
        return <CostTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} dataPeriod={dashboard.dataPeriod} orderType={dashboard.orderType} costView={costView} onCostViewChange={setCostView} />;
      case 'emissions':
        return <EmissionsTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} dataPeriod={dashboard.dataPeriod} orderType={dashboard.orderType} />;
      case 'compliance':
        return <ComplianceTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} dataPeriod={dashboard.dataPeriod} orderType={dashboard.orderType} />;
      case 'future-forecast':
        return forecastEnabled ? <FutureForecastTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} /> : null;
      case 'date-adherence':
        return <DateAdherenceTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      case 'expediting':
        return <ExpeditingTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      case 'performance-ratings':
        // CR-6381: Pass isSupplier to show different views for suppliers vs customers
        return <PerformanceRatingsTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} isSupplier={effectiveIsSupplier} />;
      case 'performance':
        // CR-6310: Performance tab with grid and chart views
        return <PerformanceTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} isSupplier={effectiveIsSupplier} />;
      default:
        return null;
    }
  };

  // Blank the page for the FIRST load only — when there is genuinely nothing to
  // show. A refetch triggered by a filter edit used to land here too, which
  // unmounted the entire tree (header, tabs, filter panel, charts) for the
  // duration of the request: measured at 2.2s on a Division change, during
  // which the panel the user was still editing was gone from the DOM. With
  // data already on screen we keep it there and revalidate underneath — the
  // inline indicator below says a refresh is in flight.
  if (dashboard.loading && !dashboard.data && !SELF_FETCHING_TABS.includes(activeTab)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading dashboard data from database...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b">
        <DashboardHeader
          viewLevel={viewLevel}
          selectedGroupId={selectedGroupId}
          isRootGroup={selectedGroupId != null && companyGroup?.root_group === selectedGroupId}
          viewLevelName={
            viewLevel === 'GROUP' ? selectedGroupName || undefined :
            viewLevel === 'SITE' ? selectedSiteName || undefined :
            selectedCompanyName || undefined
          }
        />

        {/* CR-6401: Tabs sit directly under the page header, above the filter
            header — the filter panel applies to whichever tab is selected, so
            the tab choice reads first. */}
        <div className="px-6">
          <div className="overflow-x-auto scrollbar-none">
            <div className="flex space-x-6 border-b min-w-max">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`pb-3 px-1 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* CR-6402 rework: the filter card, the view bar and the tab content
          share the page's content padding — the layout PO Lines uses, where
          the filter card sits above the table rather than inside the page
          header block. */}
      <div className="p-6 space-y-4">
        {/* CR-6401: filter header — stays visible when the sections collapse;
            the sections open inside the same card.
            CR-7312: not on Current Performance. That tab fetches its own
            payload with its own Supplier / Part filter and period tabs, so the
            page filters, Refresh and Export had no effect on it. */}
        {activeTab !== 'po-lines' && (
          <DashboardFilterBar
            savedFilters={dashboard.savedFilters}
            activeFilterPresetId={dashboard.activeFilterPresetId}
            onLoadPreset={dashboard.loadFilterPreset}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            activeFilterCount={countActiveFilters(dashboard.filters)}
            onRefresh={() => dashboard.fetchDashboardData(true)}
            onExportReport={() => dashboard.handleExportReport(activeTab)}
            exporting={dashboard.exporting}
          >
            <DashboardFilters
              filters={dashboard.filters}
              setFilters={dashboard.setFilters}
              suppliers={dashboard.suppliers}
              customers={dashboard.customers}
              sites={dashboard.sites}
              warehouses={dashboard.warehouses}
              supplierGroups={dashboard.supplierGroups}
              commodityGroups={dashboard.commodityGroups}
              commodityTypes={dashboard.commodityTypes}
              divisions={divisions}
              preferencesCurrency={dashboard.filters.currency}
              onApplyFilters={dashboard.handleApplyFilters}
              onResetFilters={dashboard.handleResetFilters}
              onShowSaveDialog={() => dashboard.setShowSaveFilterDialog(true)}
              onAutoCollapse={() => setShowFilters(false)}
              // CR-4800
              isComplianceMode={activeTab === 'compliance'}
              isCostTab={activeTab === 'cost'}
              costView={costView}
            />
          </DashboardFilterBar>
        )}

        {/* CR-6401: View bar — sits below the filter footer per the diagram. */}
          {/* CR-3930 + CR-3931: Weekly/Monthly + Orders Toggle.
              CR-4757: Compliance tab ignores dataPeriod (the prop is
              destructured as `_dataPeriod` in ComplianceTab) so the toggle
              was a no-op there — and on MAB (compliance-only tenant) it was
              the only thing showing above an unaffected dashboard. Removing
              'compliance' from the eligible tabs hides it for every tenant
              when they're on Compliance, including MAB. */}
          {['default', 'cost', 'emissions'].includes(activeTab) && (
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground mr-2">View:</span>
              <button
                onClick={() => dashboard.setDataPeriod('weekly')}
                className={`px-3 py-1 text-xs font-medium rounded-l-md border transition-colors ${
                  dashboard.dataPeriod === 'weekly'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => dashboard.setDataPeriod('monthly')}
                className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-r border-b transition-colors ${
                  dashboard.dataPeriod === 'monthly'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted'
                }`}
              >
                Monthly
              </button>

              {/* CR-4345: Orders toggle removed — order type available via filters */}
            </div>
          )}

        {/* Tab Content. Stays mounted across a filter-driven refetch; dimmed
            and marked busy while the new numbers are on their way, so the
            change is legible without tearing the page down. */}
        {isRevalidating && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite">
            <Loader2 className="h-4 w-4 animate-spin" />
            Updating results…
          </div>
        )}
        <div
          aria-busy={isRevalidating}
          className={isRevalidating ? 'opacity-60 transition-opacity' : 'transition-opacity'}
        >
          {renderTabContent()}
        </div>
      </div>

      {/* Save Filter Dialog */}
      <SaveFilterDialog
        isOpen={dashboard.showSaveFilterDialog}
        onClose={() => dashboard.setShowSaveFilterDialog(false)}
        onSave={dashboard.handleSaveFilter}
        filterName={dashboard.filterName}
        setFilterName={dashboard.setFilterName}
        filterDescription={dashboard.filterDescription}
        setFilterDescription={dashboard.setFilterDescription}
        isDefault={dashboard.isDefault}
        setIsDefault={dashboard.setIsDefault}
        isShared={dashboard.isShared}
        setIsShared={dashboard.setIsShared}
        filters={dashboard.filters}
        suppliers={dashboard.suppliers}
        customers={dashboard.customers}
        sites={dashboard.sites}
      />

      {/* Toast Notification */}
      <Toast
        message={dashboard.toast.message}
        type={dashboard.toast.type}
        visible={dashboard.toast.visible}
        onClose={() => dashboard.setToast((prev) => ({ ...prev, visible: false }))}
      />
    </div>
  );
}
